import {
  Box,
  Button,
  ColumnFilterPopover,
  DRIVES_PER_PAGE,
  FilterDropDown,
  H2,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  ListDriveInfo,
  MRT_PaginationState,
  MRT_Row,
  MRT_SortingState,
  MRT_TableOptions,
  RecordingProcessStatus,
  RowActionButton,
  PartnerRecording,
  TextInput,
  BaseRecordingTableColumn,
  baseRecordingColumnMapping,
  ModalContainer,
  useDisclosure,
  RecordingStatus,
} from '@ouster/webviz-react-components';

import { IconButton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdClear, MdRefresh, MdSearch } from 'react-icons/md';
import { TbEyeStar } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import PublicPartnerPreviewContent from '../../components/publicSample/PublicPartnerPreviewContent';
import RecordingAccessConfig from '../../components/recordingTable/RecordingAccessConfig';
import RecordingDownloadPopover from '../../components/recordingTable/RecordingDownloadPopover';
import { useColumnVisibility } from '../../components/recordingTable/useColumnVisibility';
import useFilters from '../../components/recordingTable/useFilters';
import { useSharedFromOusterListQuery } from '../../components/recordingTable/useRecordingListQuery';
import useChakraHook from '../../hooks/useChakraHook';
import { useRecordingSearch } from '../../hooks/useRecordingSearch';
import { useStoreActions, useStoreState } from '../../reducers';
import { RecordingPublicityAccessConfig } from '../../types';
import { FILTER_GROUPS } from '../../utils/constants';
import { convertFilterArrayToObject } from '../../utils/driveUtil';
import {
  createPaginationConfig,
  hasUserSelectionOnTable,
} from '../../utils/tableUtils';
import demoStyles from '../public/inAppDemo.module.css';

const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  sdk_prod_sn: false,
  sdk_prod_hw_rev: false,
};

const saveSearchToLocalStorage = (search: string, orgId: number) => {
  const searchKey = `partner_preview_${orgId}`;
  localStorage.setItem(searchKey, search);
};

type RowActionsProps = {
  row: MRT_Row<ListDriveInfo>;
  copyCloudVizLink: (value: ListDriveInfo) => void;
  configRecordingPublicity: (value: ListDriveInfo) => void;
  openRecordingDownloadPopover: (
    row: ListDriveInfo,
    event?: React.MouseEvent<HTMLButtonElement>
  ) => void;
};

const RowActions = ({
  row,
  copyCloudVizLink,
  configRecordingPublicity,
  openRecordingDownloadPopover,
}: RowActionsProps) => {
  const { curUserOrg, isOusterUser } = useStoreState(
    (storeState) => storeState.user
  );

  return (
    <Box
      flexWrap="nowrap"
      display="flex"
      data-testid="partner-row-action-buttons"
    >
      {/* 
        only Ouster admin can configure sample recording
      */}
      {isOusterUser && curUserOrg?.role === 'ADMIN' && (
        <RowActionButton
          title="Configure Sample Access"
          icon={<TbEyeStar />}
          row={row.original}
          onClickHandler={configRecordingPublicity}
        />
      )}
      <RowActionButton
        title="Copy the visualization link"
        icon={<Icon icon="share" />}
        row={row.original}
        onClickHandler={copyCloudVizLink}
      />
      <RowActionButton
        title="Download recording"
        icon={<Icon icon="download" />}
        row={row.original}
        onClickHandler={openRecordingDownloadPopover}
      />
    </Box>
  );
};

export default function PrivatePartnerPreview() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { showToast } = useChakraHook();

  const { isOusterUser, curUserOrg } = useStoreState(
    (storeState) => storeState.user
  );

  // download api
  const { setSourceFileInfo, setOutputFileInfo, updateNewContentStatus } =
    useStoreActions((actions) => actions.drive);

  // filter values
  const { loadSharedFromOusterFilterOptions } = useStoreActions(
    (actions) => actions.driveList
  );
  const { sharedFromOusterFilterOptions } = useStoreState((storeState) => ({
    ...storeState.driveList,
  }));
  const filterOptions = convertFilterArrayToObject(
    sharedFromOusterFilterOptions
  );

  const {
    filters,
    orderBy,
    sortOrder,
    pageIndex,
    isHydrated,
    setOrderBy,
    setPageIndex,
    setGroupFilters,
  } = useFilters('shared_time_stamp', 'desc');

  // table sorting
  const [sorting, setSorting] = useState<MRT_SortingState>(() => [
    {
      id: orderBy || 'shared_time_stamp',
      desc: sortOrder ? sortOrder === 'desc' : true,
    },
  ]);

  // table manual pagination
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex,
    pageSize: DRIVES_PER_PAGE,
  });
  const tableContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const tableContainer = tableContainerRef.current?.querySelector(
      '.MuiTableContainer-root'
    ) as HTMLElement;
    if (tableContainer) {
      tableContainer.scrollTop = 0;
    }
  }, [pagination.pageIndex]);
  const [searchRestoredOrgId, setSearchRestoredOrgId] = useState<number | null>(
    null
  );
  const isSearchRestored =
    !!curUserOrg?.id && searchRestoredOrgId === curUserOrg.id;
  const isOrgChanging = Boolean(sessionStorage.getItem('orgChanged'));
  const isPaginationSynced = pagination.pageIndex === pageIndex;
  const isQueryEnabled =
    isHydrated && isSearchRestored && isPaginationSynced && !isOrgChanging;

  const { data, meta, isError, isRefetching, isLoading, isSuccess, refetch } =
    useSharedFromOusterListQuery(
      curUserOrg,
      orderBy,
      sortOrder,
      pagination.pageIndex,
      pagination.pageSize,
      location.search,
      isQueryEnabled
    );

  const { columnVisibility, setColumnVisibility } = useColumnVisibility({
    orgId: curUserOrg?.id,
    defaultVisibility: DEFAULT_COLUMN_VISIBILITY,
    configKey: 'share_from_ouster_column_config',
  });

  const copyToClipboard = async (text: string, type = 'Link') => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({
        title: '',
        description: `${type} copied to clipboard`,
        status: 'success',
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyCloudVizLink = (recording: ListDriveInfo) => {
    const url = `/organization/${recording.organization_id}/drive/${recording.id}`;

    copyToClipboard(`${window.location.origin}${url}`);
  };

  const {
    isOpen: isPublicitySettingOpen,
    onOpen: onPublicitySettingOpen,
    onClose: onPublicitySettingClose,
  } = useDisclosure();
  const publicityRecordingRef = useRef<
    RecordingPublicityAccessConfig | undefined
  >(undefined);

  const onPublicitySettingCloseHandler = useCallback(() => {
    publicityRecordingRef.current = undefined;

    if (localStorage.getItem('recordingAccessConfigChanged')) {
      queryClient.invalidateQueries({ queryKey: ['drive-list'] });
      queryClient.invalidateQueries({
        queryKey: ['private-public-drive-list'],
      });

      queryClient.refetchQueries({
        queryKey: ['public-public-drive-list'],
      });

      refetch();

      localStorage.removeItem('recordingAccessConfigChanged');
    }

    onPublicitySettingClose();
  }, [onPublicitySettingClose, queryClient, refetch]);

  const configRecordingPublicity = (recording: ListDriveInfo) => {
    publicityRecordingRef.current = {
      organization_id: recording.organization_id,
      id: Number(recording.id),
      publication_uuid: recording.publication_uuid,
      data_category: recording.data_category,
      sensor_info: recording.sensor_info,
      start_time: recording.start_time,
      recording_title: recording.recording_title,
    };
    onPublicitySettingOpen();
  };

  /* 
    Click on download button will triggered a popover with all downloadable links
  */
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [downloadRecordingId, setDownloadRecordingId] = useState('');
  const downloadRecordingOrgIdRef = useRef<number | undefined>(undefined);
  const downloadRecordingTitleRef = useRef<string | undefined>(undefined);
  const downloadRecordingIsSlamProcessingRef = useRef(false);

  const openRecordingDownloadPopover = (
    recording: ListDriveInfo,
    event?: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (!event || !recording.id) return;
    setAnchorEl(event.currentTarget);
    setDownloadRecordingId(recording.id.toString());
    downloadRecordingOrgIdRef.current = recording.organization_id ?? undefined;
    downloadRecordingTitleRef.current = recording.recording_title ?? undefined;
    downloadRecordingIsSlamProcessingRef.current =
      recording.slam_generation_status === RecordingStatus.Running;
  };

  const closeDownloadMenu = () => {
    setOutputFileInfo([]);
    setSourceFileInfo([]);
    setAnchorEl(null);
  };

  const handlePaginationChange: MRT_TableOptions<ListDriveInfo>['onPaginationChange'] =
    (updater) => {
      setPagination((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (p: MRT_PaginationState) => MRT_PaginationState)(prev)
            : updater;

        if (next.pageIndex !== prev.pageIndex) {
          setPageIndex(next.pageIndex);
        }

        return next;
      });
    };

  const handleSortingChange: MRT_TableOptions<ListDriveInfo>['onSortingChange'] =
    (updater) => {
      setSorting((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (s: MRT_SortingState) => MRT_SortingState)(prev)
            : updater;

        if (!isHydrated || !next.length) {
          return next;
        }

        const { id: accessorKey, desc } = next[0];
        if (accessorKey && !accessorKey.startsWith('mrt-')) {
          setOrderBy(accessorKey, desc ? 'desc' : 'asc');
        }

        return next;
      });
    };

  const customTableOptions: Partial<MRT_TableOptions<ListDriveInfo>> = {
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    renderRowActions: ({ row }) => (
      <RowActions
        row={row}
        copyCloudVizLink={copyCloudVizLink}
        configRecordingPublicity={configRecordingPublicity}
        openRecordingDownloadPopover={openRecordingDownloadPopover}
      />
    ),
    enableRowActions: true,
    enableColumnPinning: false,
    enableHiding: true,
    enableTopToolbar: true,
    enableSortingRemoval: false,
    rowCount: meta?.totalCount ?? 0,
    state: {
      isLoading,
      pagination,
      showLoadingOverlay: false,
      showAlertBanner: isError,
      showProgressBars: isLoading || isRefetching,
      showSkeletons: isLoading || isRefetching,
      sorting,
      columnVisibility,
    },
    ...createPaginationConfig(meta, pagination),
  };

  useEffect(() => {
    if (!isHydrated) return;

    setPagination((prev) => {
      if (prev.pageIndex === pageIndex) {
        return prev;
      }

      return { ...prev, pageIndex };
    });

    setSorting((prev) => {
      const current = prev[0];
      const nextDesc = sortOrder === 'desc';
      if (current?.id === orderBy && current?.desc === nextDesc) {
        return prev;
      }

      return [{ id: orderBy, desc: nextDesc }];
    });
  }, [isHydrated, orderBy, pageIndex, sortOrder]);

  useEffect(() => {
    if (!curUserOrg?.id || isSearchRestored) return;

    if (location.search === '') {
      const storedSearch = localStorage.getItem(
        `partner_preview_${curUserOrg.id}`
      );
      if (storedSearch) {
        navigate({ search: storedSearch }, { replace: true });
      }
    }

    setSearchRestoredOrgId(curUserOrg.id);
  }, [curUserOrg?.id, isSearchRestored, location.search, navigate]);

  useEffect(() => {
    if (
      curUserOrg?.id &&
      isHydrated &&
      isSuccess &&
      !sessionStorage.getItem('orgChanged')
    ) {
      saveSearchToLocalStorage(location.search, curUserOrg.id);
    }
  }, [isHydrated, isSuccess, location.search, curUserOrg?.id]);

  /* 
    Whenever user switch organization. We need to clear the previous filters and invalidate query
  */
  useEffect(() => {
    if (curUserOrg?.id && sessionStorage.getItem('orgChanged')) {
      queryClient.invalidateQueries({ queryKey: ['shared-from-list'] });
      // get search query based on orgId
      const storedSearch = localStorage.getItem(
        `partner_preview_${curUserOrg.id}`
      );

      navigate({
        pathname: location.pathname,
        search: storedSearch || '',
      });
      sessionStorage.removeItem('orgChanged');
    }
  }, [curUserOrg?.id, location.pathname, navigate, queryClient]);

  useEffect(() => {
    if (curUserOrg?.id) {
      loadSharedFromOusterFilterOptions(curUserOrg.id);
    }
  }, [curUserOrg?.id, loadSharedFromOusterFilterOptions]);

  // Custom in page filter function
  const filterFields = useMemo(
    () => [
      baseRecordingColumnMapping[BaseRecordingTableColumn.STATUS],
      baseRecordingColumnMapping[BaseRecordingTableColumn.DUR],
      baseRecordingColumnMapping[BaseRecordingTableColumn.ORGANIZATION],
      baseRecordingColumnMapping[BaseRecordingTableColumn.PRODUCT_TYPE],
      baseRecordingColumnMapping[BaseRecordingTableColumn.RESOLUTION],
      baseRecordingColumnMapping[BaseRecordingTableColumn.BEAM_SPACING],
      baseRecordingColumnMapping[BaseRecordingTableColumn.MODE],
      baseRecordingColumnMapping[BaseRecordingTableColumn.FW],
      baseRecordingColumnMapping[BaseRecordingTableColumn.HW],
      baseRecordingColumnMapping[BaseRecordingTableColumn.SN],
      baseRecordingColumnMapping[BaseRecordingTableColumn.FW_IMG_REV],
      baseRecordingColumnMapping[BaseRecordingTableColumn.TAGS],
      baseRecordingColumnMapping[BaseRecordingTableColumn.RECORDING_TITLE],
    ],
    []
  );

  const { searchTerm, setSearchTerm, filteredData } = useRecordingSearch(
    data,
    filterFields
  );

  // column filter component
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedColKeys, setSelectedColKeys] = useState<string[]>([]);
  const handleCloseFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const openVizPage = (recording: ListDriveInfo) => {
    if (isLoading) return;
    if (hasUserSelectionOnTable()) return;
    if (
      recording.status !== RecordingProcessStatus.Processing &&
      curUserOrg?.id
    ) {
      updateNewContentStatus({
        organizationId: curUserOrg.id,
        viewType: 'SPECIFIC_ORGS',
      });

      const url = `/organization/${recording.organization_id}/drive/${recording.id}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const clearAllFilters = () => {
    if (curUserOrg?.id) {
      localStorage.removeItem(`partner_preview_${curUserOrg.id}`);
    }

    navigate({
      pathname: location.pathname,
      search: '', // Clear search parameters
    });
  };

  const totalSelectedFilters = Object.values(filters).reduce(
    (total, filterValues) => total + filterValues.length,
    0
  );

  if (!curUserOrg) return null;

  if (isSuccess && data?.length === 0 && isEmpty(filters))
    return (
      <Box
        className={`${demoStyles['page-container']}`}
        data-testid="partner-preview-empty"
      >
        <H2 fontWeight="medium" fontSize={30}>
          Exclusive Previews for Ouster Partners
        </H2>
        <PublicPartnerPreviewContent isPrivate />
      </Box>
    );

  return (
    <>
      <Box
        p="25px 36px 0px"
        h="100%"
        overflow="hidden"
        position="relative"
        display="grid"
        gridTemplateRows="auto 1fr"
        gap="1rem"
      >
        <Box display="grid" rowGap="1rem">
          <Box
            display="flex"
            placeContent="space-between"
            alignItems="center"
            mb={6}
          >
            <H2 fontWeight="medium" fontSize={30}>
              Exclusive Previews for Ouster Partners
            </H2>
            <Button
              variant="ghost"
              colorScheme="gray"
              gap={1}
              onClick={() => refetch()}
              style={{ height: 'auto' }}
              isLoading={isRefetching}
              data-testid="partner-preview-refresh"
            >
              <MdRefresh size={18} /> Refresh List
            </Button>
          </Box>
          {/* filters and search controls */}
          <Box
            display="flex"
            gap={2}
            alignItems="center"
            flexWrap="wrap"
            mr="50px"
            zIndex={2}
            data-testid="partner-preview-filters"
          >
            <InputGroup maxW="25ch" data-testid="partner-preview-search-group">
              <InputLeftElement pointerEvents="none">
                <MdSearch size={18} />
              </InputLeftElement>
              <TextInput
                pl="2.5rem"
                pr="3.5rem"
                placeholder="Search ..."
                type="search"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                data-testid="partner-preview-search-input"
              />
              <InputRightElement>
                <IconButton
                  onClick={() => setSearchTerm('')}
                  data-testid="partner-preview-search-clear"
                >
                  <MdClear size={16} />
                </IconButton>
              </InputRightElement>
            </InputGroup>

            {FILTER_GROUPS.map((groupedFilter) => (
              <FilterDropDown
                title={groupedFilter.title}
                filterKey={groupedFilter.filterKeys}
                filterOptions={filterOptions}
                setGroupFilters={setGroupFilters}
                selectedFilters={filters}
                key={groupedFilter.title}
                data-testid={`partner-preview-filter-${groupedFilter.title
                  .toLowerCase()
                  .replace(/\s/g, '-')}`}
              />
            ))}

            {totalSelectedFilters > 0 && (
              <Button
                variant="ghost"
                colorScheme="gray"
                gap={1}
                onClick={() => clearAllFilters()}
                fontSize="12px"
                data-testid="partner-preview-clear-filters"
              >
                <MdClear size={18} /> Clear Filters
              </Button>
            )}
          </Box>
        </Box>

        <Box
          h="100%"
          overflow="hidden"
          position="relative"
          bg="rgb(29, 29, 29)"
          mt="-56px"
          zIndex={1}
          ref={tableContainerRef}
          data-testid="partner-preview-table-container"
        >
          <PartnerRecording
            key={curUserOrg?.id}
            filters={{}}
            maxHeight="calc(100vh - 290px)"
            filteredData={filteredData}
            setFilterAnchorEl={setFilterAnchorEl}
            setSelectedColKeys={setSelectedColKeys}
            openVizPage={openVizPage}
            customTableOptions={customTableOptions}
            isOuster={isOusterUser}
          />
        </Box>
      </Box>
      {anchorEl && (
        <RecordingDownloadPopover
          anchorEl={anchorEl}
          closeDownloadMenu={closeDownloadMenu}
          downloadRecordingId={downloadRecordingId}
          orgId={downloadRecordingOrgIdRef.current}
          recordingTitle={downloadRecordingTitleRef.current}
          isSlamProcessing={downloadRecordingIsSlamProcessingRef.current}
          data-testid="partner-preview-download-popover"
        />
      )}

      {filterAnchorEl && (
        <ColumnFilterPopover
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilterPopover}
          filterKey={selectedColKeys}
          filterOptions={filterOptions}
          selectedFilters={filters}
          setGroupFilters={setGroupFilters}
        />
      )}

      {publicityRecordingRef.current && (
        <ModalContainer
          isOpen={isPublicitySettingOpen}
          onClose={onPublicitySettingCloseHandler}
          header="Recording Access Configuration"
          footer={null}
          content={
            <RecordingAccessConfig
              recording={publicityRecordingRef.current}
              onClose={onPublicitySettingCloseHandler}
            />
          }
        />
      )}
    </>
  );
}
